import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThumbnailList } from '../ThumbnailList';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

const StudyItem = ({
  patientName,
  date,
  description,
  numInstances,
  modalities,
  isActive,
  onClick,
  isExpanded,
  displaySets,
  activeDisplaySetInstanceUIDs,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  viewPreset = 'thumbnails',
  ThumbnailMenuItems,
  StudyMenuItems,
  StudyInstanceUID,
  accessionNumber,
  isAssociated,
}: withAppTypes) => {
  return (
    <Accordion
      type="single"
      collapsible
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      defaultValue={isActive ? 'study-item' : undefined}
    >
      <AccordionItem value="study-item">
        <AccordionTrigger
          className={classnames(
            'hover:bg-accent bg-popover group min-h-[32px] w-full rounded py-2'
          )}
        >
          <div className="flex w-full flex-row self-stretch overflow-hidden">
            <div className="flex w-full flex-row items-start justify-between">
              <div className="flex min-w-0 flex-1 flex-col items-start text-[13px]">
                {!isAssociated && (
                  <Tooltip>
                    <TooltipContent>{patientName}</TooltipContent>
                    <TooltipTrigger
                      className="w-full"
                      asChild
                    >
                      <div className="h-[18px] w-full overflow-hidden truncate whitespace-nowrap text-left font-bold text-white">
                        {patientName}
                      </div>
                    </TooltipTrigger>
                  </Tooltip>
                )}

                {!isAssociated && (
                  <Tooltip>
                    <TooltipContent>{date}</TooltipContent>
                    <TooltipTrigger
                      className="w-full"
                      asChild
                    >
                      <div className="h-[18px] w-full max-w-[160px] overflow-hidden truncate whitespace-nowrap text-left text-white">
                        {date}
                      </div>
                    </TooltipTrigger>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipContent>
                    {accessionNumber ? `Acc. ${accessionNumber}` : ''}
                  </TooltipContent>
                  <TooltipTrigger
                    className="w-full"
                    asChild
                  >
                    <div className="h-[18px] w-full max-w-[160px] overflow-hidden truncate whitespace-nowrap text-left text-white">
                      {accessionNumber ? `Acc. ${accessionNumber}` : 'Acc. -'}
                    </div>
                  </TooltipTrigger>
                </Tooltip>

                <div className="text-muted-foreground w-full whitespace-normal break-words text-left">
                  {modalities && <span className="text-white">{modalities} </span>}
                  {description}
                </div>
              </div>
              <div className="text-muted-foreground flex flex-shrink-0 flex-col items-end justify-center self-stretch pl-[10px] text-[12px]">
                {isAssociated && (
                  <span className="mb-[2px] whitespace-nowrap rounded bg-blue-600 px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide text-white">
                    Anterior
                  </span>
                )}
                <div>{numInstances}</div>
              </div>
              {StudyMenuItems && (
                <div className="ml-2 flex items-center">
                  <StudyMenuItems StudyInstanceUID={StudyInstanceUID} />
                </div>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {isExpanded && displaySets && (
            <ThumbnailList
              thumbnails={displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  displaySets: PropTypes.array,
  activeDisplaySetInstanceUIDs: PropTypes.array,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  viewPreset: PropTypes.string,
  StudyMenuItems: PropTypes.func,
  StudyInstanceUID: PropTypes.string,
  accessionNumber: PropTypes.string,
  isAssociated: PropTypes.bool,
};

export { StudyItem };
